// src/components/homepage/HeroSection.tsx
import React, { useState } from 'react';
import { Container, Typography, Button, Box } from '@mui/material';
import { BusinessConfig, LogoPlacement } from '../../types';
import RebookSnackbar from '../booking/RebookSnackbar';
interface HeroSectionProps {
  onOpenBooking: () => void; // Prop to open the booking modal
  bookingEnabled?: boolean;
  bookingCtaLabel?: string;
  onBookAgain?: () => void;
  onForgetRecentBooking?: () => void;
  showBookAgain?: boolean;
  homepageInfo: BusinessConfig['homepage'];
  heroImageURL: string | null;
  logoPlacement: LogoPlacement;
  logoUrl: string | null;
  businessName: string;
}


const HeroSection: React.FC<HeroSectionProps> = ({
  onOpenBooking,
  bookingEnabled = true,
  bookingCtaLabel = 'Book Now',
  onBookAgain,
  onForgetRecentBooking,
  showBookAgain = false,
  homepageInfo,
  heroImageURL,
  logoPlacement,
  logoUrl,
  businessName,
}) => {

  const shouldShowLogoOverlay = (logoPlacement === 'heroOverlay' || logoPlacement === 'both') && !!logoUrl;
  const [isRebookSnackDismissed, setIsRebookSnackDismissed] = useState(false);
  const shouldShowRebookSnackbar = showBookAgain && onBookAgain && !isRebookSnackDismissed;

  return (
    <Box
      sx={{
        position: 'relative',
        minHeight: '70vh',
        backgroundImage: heroImageURL ? `url(${heroImageURL})` : 'url(https://picsum.photos/1600/900)', // fallback placeholder
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
      }}
    >
      {/* Overlay to make the text pop */}
      <Box
        sx={{
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.65)', // Dark overlay to help text visibility
          zIndex: 1, // Ensures overlay is behind the text
        }}
      />

      <Container
        sx={{
          position: 'relative',
          zIndex: 2,
          maxWidth: 'sm',
          py: { xs: 2, sm: 4, md: 6 },
        }}
      >
        {shouldShowLogoOverlay && (
          <Box
            component="img"
            src={logoUrl!}
            alt={`${businessName} logo`}
            sx={{
              display: 'block',
              width: { xs: 'clamp(160px, 60vw, 260px)', sm: 'clamp(200px, 45vw, 320px)', md: 'clamp(220px, 32vw, 360px)', lg: 'clamp(260px, 25vw, 420px)' },
              maxWidth: '100%',
              height: 'auto',
              maxHeight: { xs: '40vh', sm: '45vh', md: '50vh' },
              objectFit: 'contain',
              objectPosition: 'center',
              mb: { xs: 2, md: 3 },
              mx: 'auto',
              opacity: 1,
            }}
          />
        )}
        <Typography
          variant="h2"
          component="h2"
          sx={{
            mb: 2.5,
            fontSize: { xs: '2rem', sm: '2.4rem', md: '2.8rem', lg: '3.2rem' },
            lineHeight: { xs: 1.2, md: 1.18 },
            textShadow: '0 0 10px rgba(0,0,0,0.8), 2px 2px 4px rgba(0,0,0,0.5)',
          }}
        >
          {homepageInfo.heroTitle}
        </Typography>

        <Typography
          variant="h6"
          sx={{
            mb: 3,
            fontSize: { xs: '1.1rem', sm: '1.2rem', md: '1.3rem', lg: '1.4rem' },
            lineHeight: { xs: 1.6, md: 1.65 },
            maxWidth: '80%',
            mx: 'auto',
            textShadow: '0 0 10px rgba(0,0,0,0.8), 2px 2px 4px rgba(0,0,0,0.5)',
          }}
        >
          {homepageInfo.heroSubtitle}
        </Typography>
        <Box
          sx={{
            mt: { xs: 2, md: 3 },
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 1.5, md: 2 },
            pb: { xs: 0, md: 1 },
          }}
        >
          {bookingEnabled && (
            <Button
              aria-label="Book an appointment"
              variant="contained"
              color="secondary"
              onClick={onOpenBooking}
              sx={{
                px: 3,
                py: 1.2,
                borderRadius: '999px',
                boxShadow: 3,
                color: 'common.white',
                '&:hover': {
                  color: 'common.white',
                },
              }}
            >
              {bookingCtaLabel}
            </Button>
          )}
          <Button
            aria-label="Go to contact section"
            component="a"
            href="#contact"
            variant="outlined"
            color="inherit"
            sx={{
              px: 3,
              py: 1.2,
              borderRadius: '999px',
              borderColor: 'rgba(255,255,255,0.9)',
              color: 'common.white',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.12)', borderColor: 'white' },
            }}
          >
            Contact
          </Button>
        </Box>
      </Container>
      <RebookSnackbar
        open={shouldShowRebookSnackbar ?? false}
        onDismiss={() => setIsRebookSnackDismissed(true)}
        onBookAgain={onBookAgain!}
        onForget={onForgetRecentBooking}
      />
    </Box>
  );
};

export default HeroSection;
